import { useEffect, useState, type ReactNode } from 'react';
import { Icon } from '../lib/icons.js';
import { AppBar } from '../components/Chrome.js';
import { Panel } from '../components/ui.js';
import { detectPlatform, isStandalone, type Platform } from '../lib/format.js';
import { useHouse } from '../lib/store.js';
import { WifiCard } from '../components/WifiCard.js';
import { BUILD_ID } from '../lib/build.js';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function Glyph({ name }: { name: string }): ReactNode {
  return (
    <span className="inline-icon">
      <Icon name={name} size={13} />
    </span>
  );
}

const STEPS: Record<Platform, ReactNode[]> = {
  ios: [
    <>
      Open this page in <strong>Safari</strong>. Chrome and other browsers on iPhone cannot install
      apps.
    </>,
    <>
      Tap the share button <Glyph name="share" /> at the bottom of the screen.
    </>,
    <>
      Scroll down and choose <strong>Add to Home Screen</strong>.
    </>,
    <>Tap <strong>Add</strong>. Woodlands now sits on your home screen and opens full-screen.</>,
  ],
  android: [
    <>
      Open this page in <strong>Chrome</strong>.
    </>,
    <>
      Tap the menu <Glyph name="menu" /> in the top right.
    </>,
    <>
      Choose <strong>Install app</strong> — or <strong>Add to Home screen</strong> on older versions.
    </>,
    <>Confirm, and Woodlands appears in your app drawer like any other app.</>,
  ],
  desktop: [
    <>
      Open this page in <strong>Chrome</strong> or <strong>Edge</strong>.
    </>,
    <>
      Click the install icon <Glyph name="download" /> at the right-hand end of the address bar.
    </>,
    <>
      Choose <strong>Install</strong>. It opens in its own window, without browser chrome.
    </>,
  ],
};

const PLATFORM_LABELS: Record<Platform, string> = {
  ios: 'iPhone & iPad',
  android: 'Android',
  desktop: 'Computer',
};

/** Measures the viewport the way the layout actually sees it. */
interface Probe {
  screen: string;
  inner: string;
  visual: string;
  dvh: string;
  appVar: string;
  appBox: string;
  safeArea: string;
  standalone: string;
}

function measure(): Probe {
  const px = (css: string): number => {
    const el = document.createElement('div');
    el.style.cssText = `position:fixed;top:0;left:0;width:0;visibility:hidden;pointer-events:none;height:${css}`;
    document.body.appendChild(el);
    const h = Math.round(el.getBoundingClientRect().height);
    el.remove();
    return h;
  };
  const app = document.querySelector('.app');
  const vv = window.visualViewport;
  return {
    screen: `${window.screen.width} x ${window.screen.height}`,
    inner: `${window.innerWidth} x ${window.innerHeight}`,
    visual: vv ? `${Math.round(vv.width)} x ${Math.round(vv.height)}` : 'n/a',
    dvh: `${px('100dvh')}px`,
    appVar:
      getComputedStyle(document.documentElement).getPropertyValue('--app-h').trim() || 'unset',
    appBox: app ? `${Math.round(app.getBoundingClientRect().height)}px` : 'missing',
    safeArea: `${px('env(safe-area-inset-top)')} / ${px('env(safe-area-inset-bottom)')}`,
    standalone: String(isStandalone()),
  };
}

/**
 * Tap the build line to expand. Exists because "is the installed app even
 * running the new build?" cannot be answered from outside the device — a PWA
 * serves its cached shell on launch, so a deployed fix can look like no fix.
 * Measured in an effect, never during render.
 */
function Diagnostics(): ReactNode {
  const [open, setOpen] = useState(false);
  const [probe, setProbe] = useState<Probe | null>(null);

  useEffect(() => {
    if (open) setProbe(measure());
  }, [open]);

  return (
    <div className="diagnostics">
      <button type="button" className="build-id" onClick={() => setOpen((v) => !v)}>
        Build {BUILD_ID}
      </button>
      {open && probe && (
        <dl className="diag-rows">
          {Object.entries(probe).map(([k, v]) => (
            <div key={k}>
              <dt>{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

export function Install(): ReactNode {
  const { context, signOut } = useHouse();
  const [platform, setPlatform] = useState<Platform>(detectPlatform);
  const [installed] = useState(isStandalone);
  const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const onPrompt = (e: Event): void => {
      e.preventDefault();
      setPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  return (
    <>
      <AppBar title={installed ? 'Connect' : 'Install the app'} />
      <main className="shell stack" style={{ gap: 'var(--space-3)' }}>
        {/* Once installed there is nothing to install, so the steps go entirely
            and the page becomes what it is actually for: getting online. */}
        {!installed && (
          <>
            <Panel className="stack">
              <div className="hero-top" style={{ marginBottom: 'var(--space-4)' }}>
                <span className="hero-glyph">
                  <Icon name="device" size={22} />
                </span>
                <div>
                  <div className="hero-title">Add Woodlands to your phone</div>
                  <div className="hero-sub">
                    It opens full-screen, keeps you signed in, and behaves like a normal app.
                  </div>
                </div>
              </div>

              <div className="segmented" role="group" aria-label="Choose your device">
                {(Object.keys(PLATFORM_LABELS) as Platform[]).map((p) => (
                  <button
                    key={p}
                    type="button"
                    aria-pressed={platform === p}
                    onClick={() => setPlatform(p)}
                  >
                    {PLATFORM_LABELS[p]}
                  </button>
                ))}
              </div>
            </Panel>

            {prompt && platform !== 'ios' && (
              <button
                type="button"
                className="btn btn-primary btn-block"
                onClick={() => {
                  void prompt.prompt();
                  setPrompt(null);
                }}
              >
                <Icon name="download" size={17} />
                Install now
              </button>
            )}

            <Panel title="How to" icon="info" className="stack">
              <ol className="steps" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {STEPS[platform].map((step, i) => (
                  <li className="step" key={i}>
                    <div className="body">{step}</div>
                  </li>
                ))}
              </ol>
            </Panel>
          </>
        )}

        {context?.wifi && <WifiCard wifi={context.wifi} />}

        <Panel title="Good to know" icon="info" className="stack">
          <ul
            style={{
              margin: 0,
              paddingLeft: 18,
              color: 'var(--text-dim)',
              fontSize: 14.5,
              lineHeight: 1.6,
            }}
          >
            <li>The app talks to the house live, so you need the house wifi or mobile data.</li>
            <li>Your access ends automatically; nothing to hand back.</li>
            <li>Everything you switch on or off is logged, so nobody has to guess what happened.</li>
          </ul>
        </Panel>

        <Diagnostics />

        {context && (
          <button
            type="button"
            className="btn btn-ghost btn-block"
            style={{ marginTop: 'var(--space-4)' }}
            onClick={() => void signOut()}
          >
            Sign out of this device
          </button>
        )}
      </main>
    </>
  );
}
